import { fmtNumber, fmtPercent } from './format';
import { c, t, btn } from '../../theme';
import { REGION_ORDER, RATE_YELLOW_ABOVE, RATE_RED_FROM, rollup } from './ccGroups';

// The Summary tab of the Excel tracker this module replaces: one row per
// facility (Admin and SBC each one row), grouped West / East / Administration,
// with region subtotals and a company total. Position columns (Drivers /
// Mechanics / Operations) don't apply to Administration -- shown as N/A, and
// left out of the company position totals, same as the spreadsheet.
//
// `entries` come from ccGroups.buildCcEntries; clicking a row (or a region
// heading) calls onFocus with the selector keys it stands for.

const NA = 'N/A';

const GROUPS = [
  { title: 'Headcount Overview', tint: c.infoTint, cols: [
    { label: 'Current',         get: (m) => m.headcount },
    { label: 'Vacancies',       get: (m) => m.vacancy_count },
    { label: 'Total Headcount', get: (m) => m.total_headcount },
  ] },
  { title: 'Headcount by Position', tint: c.infoTint, positional: true, cols: [
    { label: 'Drivers',    get: (m) => m.hc_driver },
    { label: 'Mechanics',  get: (m) => m.hc_mechanic },
    { label: 'Operations', get: (m) => m.hc_operations },
  ] },
  { title: 'New Hires (35 days)', tint: c.warningTint, positional: true, cols: [
    { label: 'Drivers',    get: (m) => m.nh_driver },
    { label: 'Mechanics',  get: (m) => m.nh_mechanic },
    { label: 'Operations', get: (m) => m.nh_operations },
  ] },
  { title: 'Vacancies', tint: c.dangerTint, positional: true, cols: [
    { label: 'Drivers',    get: (m) => m.vac_driver },
    { label: 'Mechanics',  get: (m) => m.vac_mechanic },
    { label: 'Operations', get: (m) => m.vac_operations },
  ] },
  { title: 'Vacancy Rate', tint: c.canvas, rate: true, cols: [
    { label: 'Total',    get: (m) => m.vacancy_rate },
    { label: 'Mechanic', get: (m) => m.mechanic_vacancy_rate, positional: true },
  ] },
  { title: 'Days to Fill (3 mo)', tint: c.canvas, cols: [
    { label: 'Total', get: (m) => m.days_to_fill, days: true },
  ] },
];

function rateTone(value) {
  if (value >= RATE_RED_FROM)    return { background: c.dangerTint,  color: c.dangerText };
  if (value > RATE_YELLOW_ABOVE) return { background: c.warningTint, color: c.warningText };
  return { background: c.successTint, color: c.successText };
}

function Cells({ metrics, isAdmin, bold, extra }) {
  return GROUPS.flatMap((g, gi) => g.cols.map((col, ci) => {
    const positional = g.positional || col.positional;
    const na = isAdmin && positional;
    const raw = na ? null : col.get(metrics);
    const first = ci === 0;
    let style = { ...s.td, ...(first ? s.groupEdge : null), ...(bold ? s.bold : null), ...extra };
    let content;
    if (na) {
      content = <span style={s.na}>{NA}</span>;
    } else if (g.rate) {
      style = { ...style, ...s.rate, ...rateTone(raw) };
      content = fmtPercent(raw);
    } else if (col.days) {
      content = raw == null ? <span style={s.na}>—</span> : fmtNumber(raw);
    } else {
      content = fmtNumber(raw);
    }
    return <td key={`${gi}-${ci}`} style={style}>{content}</td>;
  }));
}

export default function StaffingSummaryTable({ entries, onFocus }) {
  const operational = entries.filter((e) => !e.isAdmin);
  const company = { ...rollup(entries), ...positionTotals(operational) };

  const regions = REGION_ORDER
    .map((region) => ({ region, rows: entries.filter((e) => e.region === region) }))
    .filter((r) => r.rows.length);
  const colSpan = 1 + GROUPS.reduce((n, g) => n + g.cols.length, 0);

  return (
    <div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.thTitle, textAlign: 'left' }} rowSpan={2}>Location</th>
              {GROUPS.map((g) => (
                <th key={g.title} colSpan={g.cols.length} style={{ ...s.thTitle, ...s.groupEdge, background: g.tint }}>{g.title}</th>
              ))}
            </tr>
            <tr>
              {GROUPS.flatMap((g) => g.cols.map((col, ci) => (
                <th key={`${g.title}-${col.label}`} style={{ ...s.thCol, ...(ci === 0 ? s.groupEdge : null), background: g.tint }}>{col.label}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {regions.map(({ region, rows }) => (
              <RegionBlock key={region} region={region} rows={rows} colSpan={colSpan} onFocus={onFocus} />
            ))}
            <tr>
              <td style={{ ...s.tdLabel, ...s.bold, ...s.totalRow }}>Company Grand Totals</td>
              <Cells metrics={company} bold extra={s.totalRow} />
            </tr>
          </tbody>
        </table>
      </div>
      <div style={s.note}>
        Operations is every position that is not a Driver or a Mechanic. Days to Fill averages the days from a
        requisition&apos;s Recruiting Start Date until it dropped off the Workday Job Requisitions report over the last
        3 months, so a cancelled or frozen req counts the same as a filled one; tracking began when this table shipped,
        so it fills in over time.
      </div>
    </div>
  );
}

function RegionBlock({ region, rows, colSpan, onFocus }) {
  const title = region === 'Administration' || region === 'Other' ? region : `${region} Region`;
  const showSubtotal = rows.length > 1;
  const subtotal = showSubtotal ? { ...rollup(rows), ...positionTotals(rows.filter((r) => !r.isAdmin)) } : null;
  return (
    <>
      <tr>
        <td colSpan={colSpan} style={s.region}>
          <button type="button" style={s.regionBtn} onClick={() => onFocus(rows.map((r) => r.key))}>{title}</button>
        </td>
      </tr>
      {rows.map((row, i) => (
        <tr key={row.key} style={{ background: i % 2 ? c.surfaceSunk : c.surface }}>
          <td style={s.tdLabel}>
            <button type="button" style={btn.link} onClick={() => onFocus([row.key])}>{row.label}</button>
          </td>
          <Cells metrics={row} isAdmin={row.isAdmin} />
        </tr>
      ))}
      {showSubtotal && (
        <tr style={s.subtotalRow}>
          <td style={{ ...s.tdLabel, ...s.bold, textAlign: 'right' }}>{title.replace(' Region', '')} Grand Totals</td>
          <Cells metrics={subtotal} bold />
        </tr>
      )}
    </>
  );
}

// Position columns skip Administration (N/A there), so their totals are summed
// over the operational entries only; the rate columns stay on the full rollup.
function positionTotals(operationalEntries) {
  const r = rollup(operationalEntries);
  return {
    hc_driver: r.hc_driver, hc_mechanic: r.hc_mechanic, hc_operations: r.hc_operations,
    nh_driver: r.nh_driver, nh_mechanic: r.nh_mechanic, nh_operations: r.nh_operations,
    vac_driver: r.vac_driver, vac_mechanic: r.vac_mechanic, vac_operations: r.vac_operations,
    mechanic_vacancy_rate: r.mechanic_vacancy_rate,
  };
}

const s = {
  scroll:   { overflowX: 'auto' },
  table:    { width: '100%', borderCollapse: 'collapse', fontSize: t.bodySm.fontSize },
  thTitle:  { padding: '8px 10px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: c.textStrong, borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk, whiteSpace: 'nowrap' },
  thCol:    { padding: '7px 7px', textAlign: 'right', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px', color: c.textMuted, borderBottom: `1px solid ${c.border}`, lineHeight: 1.25 },
  groupEdge: { borderLeft: `1px solid ${c.border}` },
  td:       { padding: '8px 7px', textAlign: 'right', color: c.textStrong, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  tdLabel:  { padding: '8px 14px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  rate:     { fontWeight: 600, textAlign: 'center' },
  na:       { color: c.textFaint },
  bold:     { fontWeight: 700 },
  region:   { padding: '6px 14px', background: c.canvas, borderBottom: `1px solid ${c.border}` },
  regionBtn: { all: 'unset', cursor: 'pointer', fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: c.textStrong },
  subtotalRow: { background: c.canvas },
  totalRow: { borderTop: `2px solid ${c.borderStrong}` },
  note:     { padding: '10px 16px', fontSize: t.caption.fontSize, color: c.textFaint, borderTop: `1px solid ${c.border}` },
};
